import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { APPS } from "../../../../constants/AppsConstants.ts";
import { useAppStore } from "../../../../hooks/useStores";
import { ImageEditingViewMode, ImageToImageConfigType, PlaygroundImage } from "../../../../models/image-to-image";
import { TextToImageConfigType } from "../../../../models/text-to-image.ts";
import { isFiboApp } from "../../../../utils/index.ts";
import iframeStore from "../../../IframeNew/iframe-store.tsx";
import ImageOverlayEditorButton from "./EditorButton/ImageOverlayEditorButton.tsx";
import ImageOverlayFeedbackButtons from "./FeedbackButtons/ImageOverlayFeedbackButtons.tsx";
import styles from "./PlaygroundImageOverlay.module.scss";
import ImageOverlayRefineButton from "./RefineButton/ImageOverlayRefineButton.tsx";
import ImageOverlayShareButton from "./ShareButton/ImageOverlayShareButton.tsx";

export type PlaygroundImageOverlayProps = {
	image: PlaygroundImage;
	hideAiEditorButton?: boolean;
	hideFeedbackButtons?: boolean;
	hideApiReferenceButton?: boolean;
	thumbUpIcon?: React.ComponentType<any>;
	thumbDownIcon?: React.ComponentType<any>;
	highlightFeedbackButtons?: boolean;
	actionButtonsBottom?: React.ReactNode;
};

export type Feedback = {
	appName: string;
	type: string;
	imageUrl: string;
	text?: string;
	feedback_issues?: string;
	modelName?: string;
	modelId?: string;
	ui_type?: string;
	seed?: number;
	configs?: TextToImageConfigType | ImageToImageConfigType;
	refImageUrl?: string;
	mode?: string;
	image_config?: TextToImageConfigType | ImageToImageConfigType;
};

export enum FeedbackTypeEnum {
	Positive = "Positive",
	Negative = "Negative",
}

const PlaygroundImageOverlay = ({
	image,
	hideAiEditorButton = false,
	hideFeedbackButtons = false,
	thumbUpIcon,
	thumbDownIcon,
	highlightFeedbackButtons = false,
	actionButtonsBottom,
}: PlaygroundImageOverlayProps) => {
	const { playgroundStore } = useAppStore();

	const isSvg = (image.config as TextToImageConfigType)?.format === "svg";
	const isCampaign = image.type === APPS.CAMPAIGN;
	const isTextToImage = image.type === "textToImage";
	const isTgImageGen = image.type === "tgImageGeneration";
	const isUploaded = image.type === "upload";
	const isIframe = iframeStore.isIframe();
	const isFeedbackEnabled = iframeStore.iframe.config.general_config?.enable_feedback_buttons;
	const isImageToImageEnabled = iframeStore.iframe.config.enabled_pages.imageToImage;
	const showAiEditorButton =
		!isSvg &&
		!isCampaign &&
		!hideAiEditorButton &&
		!playgroundStore.isText2ImagePopupOpened &&
		(!isIframe || isImageToImageEnabled) &&
		(playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE ||
			playgroundStore.imageEditingViewMode === ImageEditingViewMode.GALLERY);

	const showRefineButton =
		(isTextToImage || isTgImageGen) &&
		!isSvg &&
		!isCampaign &&
		(playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE ||
			playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION) &&
		!isFiboApp() &&
		!(
			isTgImageGen &&
			(image?.config as TextToImageConfigType)?.tailored_model_id &&
			(image?.config as TextToImageConfigType)?.enhance_image
		);

	const showFeedbackButtons = (!isIframe || isFeedbackEnabled) && !isCampaign && !isUploaded && !hideFeedbackButtons;

	return (
		<Box className={clsx(styles.container, "container")}>
			{showAiEditorButton && (
				<Box className={styles.aiEditorButtonTopRight}>
					<ImageOverlayEditorButton image={image} />
				</Box>
			)}
			<Box
				className={clsx(styles.imageFooter, {
					[styles.singleMode]: playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE,
				})}
			>
				{actionButtonsBottom}
				{showRefineButton && <ImageOverlayRefineButton image={image} />}
				{showAiEditorButton &&
					playgroundStore.selectedConfig !== APPS.TEXT_TO_IMAGE &&
					playgroundStore.selectedConfig !== APPS.TG_IMAGE_GENERATION && (
						<ImageOverlayEditorButton image={image} />
					)}
				{isTextToImage && <ImageOverlayShareButton />}
				{showFeedbackButtons && (
					<ImageOverlayFeedbackButtons
						image={image}
						thumbUpIcon={thumbUpIcon}
						thumbDownIcon={thumbDownIcon}
						highlightFeedbackButtons={highlightFeedbackButtons}
					/>
				)}
			</Box>
		</Box>
	);
};

export default observer(PlaygroundImageOverlay);
